import React from 'react';
import { ExternalLink } from 'lucide-react';

/**
 * Render plain text with two lightweight markdown features:
 *   • **bold** → <strong>bold</strong>
 *   • https://... → clickable external link (yellow, opens in new tab)
 *
 * Shared by UserDashboard (10 Daily GRIT Practices) and MyActions
 * so admin-authored action descriptions render consistently.
 * The two syntaxes are non-overlapping — URLs are extracted first,
 * then any remaining plain-text segments are scanned for **bold**.
 */
export const renderTextWithLinks = (text) => {
  if (!text) return null;

  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#EFB342] hover:underline inline-flex items-center gap-0.5"
          onClick={(e) => e.stopPropagation()}
        >
          {part.length > 40 ? part.substring(0, 40) + '...' : part}
          <ExternalLink size={12} className="inline ml-0.5" />
        </a>
      );
    }
    return <span key={index}>{renderBold(part, index)}</span>;
  });
};

// Split a plain-text chunk on **...** and wrap matches in <strong>.
// Odd-indexed segments are the bolded content (split() returns
// [before, match, after, match, after, ...] with a capture group).
const renderBold = (text, parentKey) => {
  if (!text) return null;
  const segments = text.split(/\*\*(.+?)\*\*/g);
  return segments.map((seg, i) =>
    i % 2 === 1
      ? <strong key={`${parentKey}-b-${i}`}>{seg}</strong>
      : <React.Fragment key={`${parentKey}-t-${i}`}>{seg}</React.Fragment>
  );
};
